import { useState } from 'react';
import { X, GitCompare, Plus } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { brand } from '@/lib/theme/tokens';
import { unsplash } from '@/lib/utils/img';
import { useCompare } from '@/hooks/useCompare';
import { CompareModal } from './CompareModal';

export function CompareBar() {
  const { items, remove, clear } = useCompare();
  const [modalOpen, setModalOpen] = useState(false);

  const visible = items.length > 0;

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            key="compare-bar"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 34 }}
            className="fixed bottom-5 left-1/2 z-[150] -translate-x-1/2"
          >
            <div
              className="flex items-center gap-3 rounded-2xl px-4 py-3"
              style={{
                background: 'var(--bg)',
                border: '1px solid var(--glass-edge-hi)',
                boxShadow: '0 16px 48px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)',
                minWidth: 320,
              }}
            >
              {/* Thumbnails */}
              <div className="flex items-center gap-2">
                {items.map((item) => (
                  <div key={item.id} className="relative">
                    <div className="h-10 w-10 overflow-hidden rounded-lg ring-1 ring-[var(--glass-edge-hi)]">
                      <img
                        src={unsplash(item.image, { w: 120 })}
                        alt={item.name}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(item.id)}
                      aria-label={`Remove ${item.name}`}
                      className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full text-white"
                      style={{ background: brand.chinar }}
                    >
                      <X size={10} strokeWidth={3} />
                    </button>
                  </div>
                ))}

                {/* Empty slot dots */}
                {Array.from({ length: 3 - items.length }).map((_, i) => (
                  <div
                    key={i}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-dashed"
                    style={{ borderColor: 'var(--glass-edge-hi)' }}
                  >
                    <Plus size={13} className="text-ink-dim" />
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="h-8 w-px" style={{ background: 'var(--glass-edge)' }} />

              {/* Count label */}
              <div className="flex-1">
                <p className="text-[13px] font-semibold text-ink">
                  {items.length} of 3 selected
                </p>
                <button
                  type="button"
                  onClick={clear}
                  className="text-[11px] text-ink-mute underline-offset-2 transition hover:underline"
                >
                  Clear all
                </button>
              </div>

              {/* Compare button */}
              <button
                type="button"
                disabled={items.length < 2}
                onClick={() => setModalOpen(true)}
                className="flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-bold text-white transition disabled:opacity-40"
                style={{ background: items.length >= 2 ? brand.teal : 'var(--glass-hi)' }}
              >
                <GitCompare size={14} />
                Compare
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CompareModal
        open={modalOpen}
        items={items}
        onRemove={remove}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}
